import { skipToken } from '@reduxjs/toolkit/query'
import { formatDate } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/button'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { ConfirmRefundActionModal } from '../../components/modal/app_modals/confirmRefundActionModal'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import { NUMBER_OF_ITEMS_PER_PAGE, RefundStatusMap } from '../../constants'
import { formatCurrency } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import { useGetAllRefundsQuery } from '../../redux-toolkits/transactions/transactions.slice'
import {
  GetAllRefundsQuery,
  RefundStatus,
} from '../../redux-toolkits/refunds/refunds.type'

const RefundsPage: React.FC = () => {
  const [query, setQuery] = useState<GetAllRefundsQuery>({
    page: 1,
    limit: NUMBER_OF_ITEMS_PER_PAGE,
    search: undefined,
    status: undefined,
    sort: 'desc',
  })
  const [refundAction, setRefundAction] = useState<'reject' | 'approve'>(
    'approve',
  )
  const [selectedRefund, setSelectedRefund] = useState<string>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const navigate = useNavigate()

  const { data: refunds, isFetching: fetchingRefunds } = useGetAllRefundsQuery(
    query
      ? {
          ...query,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  )

  useEffect(() => {
    setQuery({
      page: Number(searchParams.get('page') || 1),
      limit: 20,
      search: searchParams.get('search') || undefined,
      status: (searchParams.get('status') as RefundStatus) || undefined,
      sort: 'desc',
    })
  }, [searchParams])

  return (
    <Layout>
      <div className="flex items-center justify-between gap-4 mb-10">
        <PageHeader title={`Refunds`} className="capitalize !mb-0" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div className="!w-full md:!max-w-[400px]">
          <SearchInput
            searchFunction={(search) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                search: search,
                page: '1',
              })
            }}
            placeholder="Search"
            initialValue={searchParams.get('search') || undefined}
          />
        </div>

        <Dropdown
          menuClassName=""
          menuButton={
            <Button
              label={`Filter: ${query?.status?.toLowerCase() ?? 'all'}`}
              primary={false}
              icon="filter"
              className="!font-normal ml-auto"
            />
          }
          menuItems={[
            {
              name: 'All',
              value: 'all',
            },
            ...Object.keys(RefundStatusMap).map((key) => {
              return {
                name: RefundStatusMap[key as RefundStatus].name,
                value: key,
              }
            }),
          ].map((filter) => ({
            ...filter,
            onClick: () => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                status:
                  filter.value === 'all' ? '' : (filter.value as RefundStatus),
                page: '1',
              })
            },
          }))}
        />
      </div>

      <div className="w-full overflow-x-scroll">
        <TableComponent
          headers={[
            'CUSTOMER NAME',
            'ORDER ID',
            'AMOUNT',
            'STATUS',
            'DATE',
            'ACTIONS',
          ]}
          rows={
            refunds?.data?.docs?.map((row, idx) => {
              return {
                id: idx,
                content: [
                  <Link
                    key={`${idx}-order`}
                    to={`/customers/${row?.user?._id}`}
                    className="hover:underline cursor-pointer !capitalize"
                  >
                    {row?.user?.firstname} {row?.user?.lastname}{' '}
                    {!row?.user?.lastname && !row?.user?.lastname ? 'N/A' : ''}
                  </Link>,
                  <Link
                    key={`${idx}-order`}
                    to={`/refunds/${row?.order?._id}`}
                    className="hover:underline cursor-pointer"
                  >
                    #{row?.order?.orderNumber}
                  </Link>,
                  formatCurrency(row.requestedRefundAmount),
                  <Status
                    type={RefundStatusMap[row.status].pillVariant}
                    text={RefundStatusMap[row.status].name}
                    key={`${idx}-status`}
                  />,
                  formatDate(row.createdAt, 'dd/MM/yyyy h:mma'),
                  <Dropdown
                    key={`${idx}-controls`}
                    menuButton={
                      <Icon id="ellipses" height={18} width={18} className="" />
                    }
                    menuItems={[
                      {
                        name: 'View Details',
                        value: '',
                        onClick: () => {
                          navigate(`/refunds/${row?.order?._id}`)
                        },
                      },
                      {
                        name: <p>Approve Refund</p>,
                        value: '',
                        hidden: row.status !== 'PENDING',
                        onClick: () => {
                          setSelectedRefund(row._id)
                          setRefundAction('approve')
                          handleModalOpen('refundActionmodal')
                        },
                      },
                      {
                        name: <p>Decline Refund</p>,
                        value: '',
                        hidden: row.status !== 'PENDING',
                        onClick: () => {
                          setSelectedRefund(row._id)
                          setRefundAction('reject')
                          handleModalOpen('refundActionmodal')
                        },
                      },
                    ]}
                  />,
                ],
              }
            }) ?? []
          }
          isEmpty={[0, undefined, ''].includes(refunds?.data?.docs?.length)}
          loading={fetchingRefunds}
          currentPage={Number(query.page ?? 0)}
          totalDataCount={refunds?.data?.total}
          pageLimit={Number(query.limit ?? 0)}
          onPageChange={(page) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: `${page}`,
            })
          }}
        />
      </div>

      {selectedRefund && (
        <ConfirmRefundActionModal
          open={modal === 'refundActionmodal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            handleModalClose()
          }}
          onSuccessCallback={() => {
            handleModalClose()
            setSelectedRefund(undefined)
          }}
          refundId={selectedRefund}
          actionType={refundAction}
        />
      )}
    </Layout>
  )
}

export default RefundsPage
